import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, PenLine, RefreshCw, Search, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ForumService } from '../services/others/forumService';
import { ForumCategory, ForumPostWithComments } from '../types/forum';
import useSeoMeta from '../hooks/useSeoMeta';
import seoPaginas from '../content/seoPaginas.json';
import PublicNav from '../components/common/PublicNav';
import PublicFooter from '../components/common/PublicFooter';
import { Eyebrow } from '../components/landing/brand';

// /foro — manual de marca v1.0 (estilos .lp-forum / .lp-topic en brand.css).
// El botón "Publicar un tema" conserva data-ar-guide="foro-btn-publicar" (tutorial).

const ForumPage: React.FC = () => {
  const { user } = useAuth();

  useSeoMeta(seoPaginas.foro);

  const [posts, setPosts] = useState<ForumPostWithComments[]>([]);
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');

  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategoryId, setNewCategoryId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isCreateFormExpanded, setIsCreateFormExpanded] = useState(false);

  const filteredPosts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter((p) => {
      const matchesQuery =
        q.length === 0 ||
        p.title.toLowerCase().includes(q) ||
        p.content.toLowerCase().includes(q) ||
        p.author_name.toLowerCase().includes(q);
      const matchesCategory = !categoryId || p.category_id === categoryId;
      return matchesQuery && matchesCategory;
    });
  }, [posts, search, categoryId]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const [cats, ps] = await Promise.all([
        ForumService.getCategories(),
        ForumService.getAllPosts(50, 0),
      ]);

      setCategories(cats);
      setPosts(ps);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando el foro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);

    if (!user) {
      setSaveError('Tenés que iniciar sesión para publicar.');
      return;
    }

    if (!newTitle.trim() || !newContent.trim()) {
      setSaveError('Completá título y contenido.');
      return;
    }

    try {
      setSaving(true);

      const created = await ForumService.createPost(
        newTitle.trim(),
        newContent.trim(),
        user.id,
        user.email || 'Usuario',
        newCategoryId || undefined
      );

      if (!created) {
        setSaveError('No se pudo crear el post (revisá permisos / sesión).');
        return;
      }

      setNewTitle('');
      setNewContent('');
      setNewCategoryId('');

      await load();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Error creando el post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lp">
      <PublicNav />

      <main>
        <section className="lp-ph" style={{ paddingBottom: 48 }}>
          <div className="lp-wrap">
            <Eyebrow>Comunidad</Eyebrow>
            <h1>Foro ganadero</h1>
            <p className="lp-lead" style={{ maxWidth: 680 }}>
              Preguntas, respuestas y debate entre productores: cría, engorde, sanidad y manejo. Leé lo que ya se
              charló o contá tu caso.
            </p>
            <div className="lp-ctl-row" style={{ marginTop: 28, maxWidth: 560, position: 'relative' }}>
              <Search
                aria-hidden="true"
                size={18}
                style={{ position: 'absolute', left: 14, color: 'var(--lp-piedra)', pointerEvents: 'none' }}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="lp-control"
                style={{ paddingLeft: 40, height: 50 }}
                placeholder="Buscar por título, contenido o autor..."
                aria-label="Buscar en el foro"
              />
            </div>
          </div>
        </section>

        <section className="lp-sec lp-sec--white" style={{ paddingTop: 48 }}>
          <div className="lp-wrap lp-forum">
            {/* Categorías */}
            <aside>
              <p className="lp-meta" style={{ marginBottom: 10 }}>Categorías</p>
              <div className="lp-cats">
                <button type="button" className={categoryId === '' ? 'on' : undefined} onClick={() => setCategoryId('')}>
                  Todas <small>{posts.length}</small>
                </button>
                {categories.map((cat) => {
                  const catPosts = posts.filter((p) => p.category_id === cat.id).length;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      className={categoryId === cat.id ? 'on' : undefined}
                      onClick={() => setCategoryId(cat.id)}
                    >
                      {cat.name} <small>{catPosts}</small>
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="lp-stack">
              {/* Publicar */}
              <div className="lp-card">
                <button
                  type="button"
                  onClick={() => setIsCreateFormExpanded(!isCreateFormExpanded)}
                  data-ar-guide="foro-btn-publicar"
                  aria-expanded={isCreateFormExpanded}
                  className="lp-link"
                  style={{ width: '100%', justifyContent: 'space-between', fontSize: 17 }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <PenLine aria-hidden="true" /> Publicar un tema
                  </span>
                  {isCreateFormExpanded && <X aria-hidden="true" />}
                </button>

                {isCreateFormExpanded && (
                  <form onSubmit={handleCreatePost} className="lp-stack" style={{ marginTop: 18 }}>
                    {!user && (
                      <p className="lp-note" style={{ fontSize: 14 }}>
                        Para publicar y comentar, necesitás{' '}
                        <Link className="lp-inline" to="/login">
                          iniciar sesión
                        </Link>
                        .
                      </p>
                    )}
                    <div className="lp-fields lp-fields--3">
                      <label className="lp-field">
                        <span>Título</span>
                        <input
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          disabled={!user || saving}
                          className="lp-control"
                          placeholder="Ej: Estrategia de suplementación con sequía"
                        />
                      </label>
                      <label className="lp-field">
                        <span>Categoría</span>
                        <select
                          value={newCategoryId}
                          onChange={(e) => setNewCategoryId(e.target.value)}
                          disabled={!user || saving}
                          className="lp-control"
                        >
                          <option value="">(Sin categoría)</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label className="lp-field">
                      <span>Contenido</span>
                      <textarea
                        value={newContent}
                        onChange={(e) => setNewContent(e.target.value)}
                        disabled={!user || saving}
                        rows={5}
                        className="lp-control"
                        placeholder="Contá tu situación, zona, categoría, peso/edad, y qué alternativas estás evaluando..."
                      />
                    </label>

                    {saveError && <div className="lp-alert">{saveError}</div>}

                    <div className="lp-row-actions">
                      <button type="button" className="lp-btn lp-btn--ghost lp-btn--sm" onClick={() => setIsCreateFormExpanded(false)}>
                        Cancelar
                      </button>
                      <button type="submit" className="lp-btn lp-btn--primary lp-btn--sm" disabled={!user || saving}>
                        {saving ? 'Publicando...' : 'Publicar'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Temas */}
              <div className="lp-sec__h" style={{ marginBottom: 0, marginTop: 8, alignItems: 'center' }}>
                <h2 className="lp-h3" style={{ fontSize: 22 }}>Temas recientes</h2>
                <button type="button" className="lp-btn lp-btn--ghost lp-btn--xs" onClick={load}>
                  <RefreshCw aria-hidden="true" /> Actualizar
                </button>
              </div>

              {loading && <div className="lp-skel" style={{ height: 120 }} />}
              {error && <div className="lp-alert">{error}</div>}

              {!loading && !error && filteredPosts.length === 0 && (
                <div className="lp-card lp-empty">
                  <h3>{posts.length === 0 ? 'Todavía no hay temas' : 'No encontramos temas'}</h3>
                  <p>{posts.length === 0 ? 'Sé el primero en abrir una charla.' : 'Probá con otra búsqueda o categoría.'}</p>
                </div>
              )}

              {!loading && !error && (
                <div className="lp-topics">
                  {filteredPosts.map((post) => {
                    const category = categories.find((c) => c.id === post.category_id);
                    return (
                      <Link key={post.id} to={`/foro/${post.id}`} className="lp-topic">
                        <div className="lp-topic__h">
                          <h3>{post.title}</h3>
                          <span className="lp-count">
                            <MessageSquare aria-hidden="true" /> {post.comments_count}
                            <span className="sr-only"> comentarios</span>
                          </span>
                        </div>
                        <p>{post.content.length > 180 ? `${post.content.slice(0, 180)}...` : post.content}</p>
                        <footer>
                          {category && <span className="lp-badge">{category.name}</span>}
                          <span>
                            Por <b>{post.author_name}</b> · {new Date(post.created_at).toLocaleString('es-AR')}
                          </span>
                        </footer>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default ForumPage;
